/**
 * Rentals list WebSocket live updates (templates/propmgmt/rentals/list.html).
 *
 * Trimmed copy of acqs-ws.js: connects to /ws/rentals/ while the modern rentals
 * list is on the page (`#list-results-wrapper[data-row-qs]` is the marker), and on
 * `rental_updated` re-fetches that rental's row (`rental-row`, with the tab +
 * filters from `window.rentalsListRowQs()`) and swaps it over `#rental-<pk>`.
 * A 404 means the rental left this viewer's tab/filters: the row is removed. A
 * rental that is not on the page is ignored -- it appears on the next list load.
 *
 * Self-echo: every htmx request from the list carries this tab's id in
 * `X-Rentals-Tab-Id`; the broadcast hands it back, and a message tagged with our
 * own id is skipped -- the POST response (the row, or the `rentalChanged` list
 * refresh) already painted this tab.
 */

(function () {
    var TAB_ID_HEADER = 'X-Rentals-Tab-Id';
    var rentalsTabId = (
        window.crypto && window.crypto.randomUUID
            ? window.crypto.randomUUID()
            : String(Date.now()) + '-' + String(Math.random()).slice(2)
    );

    function listWrapper() {
        return document.querySelector('#list-results-wrapper[data-row-qs]');
    }

    function isOnRentalsList() {
        return !!listWrapper();
    }

    document.addEventListener('htmx:configRequest', function (event) {
        if (!isOnRentalsList()) return;
        event.detail.headers[TAB_ID_HEADER] = rentalsTabId;
    });

    // ── Row refresh ────────────────────────────────────────────────────────

    // Hold a swap while the viewer has a dropdown open or is typing inside that
    // row (the next-action editor and the tags panel live in the row), re-check
    // as focus and clicks move, and apply once the row is idle. Same guard as the
    // acqs deal rows: a swap under a typing user destroys their text with no toast.
    var ROW_SWAP_RETRY_MS = 400;
    var pendingRowSwaps = {};
    var pendingRowSwapTimer = null;

    function isTextEntry(el) {
        if (!el || !el.matches) return false;
        if (el.isContentEditable) return true;
        if (!el.matches('input, textarea, select')) return false;
        return !el.matches('input[type=checkbox], input[type=radio], input[type=button], input[type=submit], input[type=reset]');
    }

    function rowIsBusy(row) {
        if (!row) return false;
        if (row.querySelector('.dropdown.show')) return true;
        var active = document.activeElement;
        return !!(active && isTextEntry(active) && row.contains(active));
    }

    function runPendingRowSwaps() {
        Object.keys(pendingRowSwaps).forEach(function (elementId) {
            if (rowIsBusy(document.getElementById(elementId))) return;
            var action = pendingRowSwaps[elementId];
            delete pendingRowSwaps[elementId];
            action();
        });
        if (pendingRowSwapTimer && !Object.keys(pendingRowSwaps).length) {
            clearInterval(pendingRowSwapTimer);
            pendingRowSwapTimer = null;
        }
    }

    function deferWhileEditing(elementId, action) {
        if (!rowIsBusy(document.getElementById(elementId))) {
            action();
            return;
        }
        pendingRowSwaps[elementId] = action;
        if (!pendingRowSwapTimer) {
            pendingRowSwapTimer = setInterval(runPendingRowSwaps, ROW_SWAP_RETRY_MS);
        }
    }

    function checkPendingRowSwapsSoon() {
        if (!pendingRowSwapTimer) return;
        setTimeout(runPendingRowSwaps, 0);
    }

    document.addEventListener('focusout', checkPendingRowSwapsSoon);
    document.addEventListener('click', checkPendingRowSwapsSoon);

    function removeRow(row) {
        if (!row) return;
        var tbody = row.closest('tbody');
        row.remove();
        if (!tbody || tbody.querySelector('tr')) return;
        // Same placeholder every list/_table_<set>.html renders for {% empty %}.
        tbody.insertAdjacentHTML('beforeend',
            '<tr><td colspan="20"><div class="data-table__empty">' +
            '<i class="fa-solid fa-house-circle-xmark"></i> No rentals match criteria</div></td></tr>');
    }

    // The element is re-read by id inside apply(): the fetch is async and the
    // apply can be deferred, and either can run after an htmx swap replaced the
    // node. `outerHTML` + `htmx.process(parent)` is the same swap acqs-ws.js does
    // for its rows, so the new row's `hx-*` attributes are live.
    function refreshRentalRow(rentalId) {
        var elementId = 'rental-' + rentalId;
        if (!document.getElementById(elementId)) return;
        var xhr = new XMLHttpRequest();
        xhr.open('GET', '/propmgmt/rentals/' + rentalId + '/row/?' + window.rentalsListRowQs());
        xhr.setRequestHeader('HX-Request', 'true');
        xhr.setRequestHeader('HX-Current-URL', window.location.href);
        xhr.onload = function () {
            var apply;
            if (xhr.status >= 200 && xhr.status < 300 && xhr.responseText.trim()) {
                apply = function () {
                    var live = document.getElementById(elementId);
                    if (!live) return;
                    var parent = live.parentNode;
                    if (!parent) return;
                    live.outerHTML = xhr.responseText;
                    if (window.htmx) htmx.process(parent);
                };
            } else if (xhr.status === 404) {
                apply = function () { removeRow(document.getElementById(elementId)); };
            } else {
                return;
            }
            deferWhileEditing(elementId, apply);
        };
        xhr.send();
    }

    // ── WebSocket connection ───────────────────────────────────────────────

    var socket = null;
    var reconnectTimer = null;
    var reconnectAttempts = 0;

    function connect() {
        if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }

        var protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
        socket = new WebSocket(protocol + '//' + location.host + '/ws/rentals/');

        socket.onopen = function () {
            reconnectAttempts = 0;
        };

        socket.onmessage = function (e) {
            var data = JSON.parse(e.data);
            if (data.type !== 'rental_updated') return;
            if (data.tab_id && data.tab_id === rentalsTabId) return;
            refreshRentalRow(data.rental_id);
        };

        socket.onclose = function (e) {
            socket = null;
            if (e.code === 4401) {
                window.location.href = '/login/?next=' + encodeURIComponent(window.location.pathname);
                return;
            }
            // 4403: no `propmgmt:rentals` permission. Retrying cannot change that.
            if (e.code === 4403 || !isOnRentalsList()) return;
            if (reconnectAttempts < 10) {
                var delay = Math.min(2000 * Math.pow(1.5, reconnectAttempts), 30000);
                reconnectAttempts++;
                reconnectTimer = setTimeout(connect, delay);
            }
        };
    }

    function disconnect() {
        if (socket) {
            socket.close();
            socket = null;
        }
        if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
    }

    // Marker-based: connect while the list is on the page, tear down when an
    // HTMX navigation (or back/forward) swaps it out. `htmx:afterSettle` covers
    // history restores, which swap the content after `popstate` has fired.
    function checkConnection() {
        if (isOnRentalsList()) {
            if (!socket) {
                reconnectAttempts = 0;
                connect();
            }
        } else if (socket || reconnectTimer) {
            disconnect();
        }
    }

    checkConnection();
    document.body.addEventListener('htmx:pushedIntoHistory', checkConnection);
    document.body.addEventListener('htmx:afterSettle', checkConnection);
    window.addEventListener('popstate', checkConnection);

})();
